import * as Sentry from '@sentry/nextjs';
import { dropHiddenDrawRows } from '@/app/[locale]/event/[eventId]/libs/hiddenDraws';
import { isPlayedSchedule } from '@/app/[locale]/admin/event-management/[eventId]/edit/scheduler/libs/isPlayedSchedule';
import type { CourtDefinition } from '@/app/[locale]/admin/event-management/[eventId]/edit/scheduler/match-scheduler-types';
import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@/types/supabase';
import { getSupabaseAdminClient } from '@/lib/supabase/admin';
import { createClient } from '@/lib/supabase/client';
import { playerModelV2 } from '@/models/playerModelV2';
import { isEventAliasAllowed } from '@/models/eventModel';
import { locationServerModel } from '@/models/locationServerModel';
import { filterRegisteredEventsRefEligible } from '@/app/[locale]/match/libs/refEligibility';
import { deriveScorerScopes } from '@/lib/scorerScopes';
import { dayKeyFor, dayKeyInZone } from '@/lib/dayKey';
import { dayBoundsUtc, isDayKey } from '@/lib/dayBoundsUtc';
import type { ScheduleContext } from '@/app/[locale]/event/[eventId]/libs/scheduleContext';
import { normaliseTimezone } from '@/lib/zoneOffsetAt';
import { loadVenueContextForEvent } from '@/app/[locale]/event/[eventId]/libs/venuesLoad';
import { selectAllRows } from '@/lib/supabase/batchedQueries';
import { gateRowAliases } from '@/app/[locale]/event/[eventId]/libs/aliasGate';
import { buildEventTeamEntryMap } from '@/app/[locale]/event/[eventId]/libs/teamLabels';
import {
  projectScheduleCourts,
  projectScheduleRows,
  type ScheduleSourceRow,
} from '@/app/[locale]/event/[eventId]/libs/projectScheduleRows';

type MatchSchedule = Database['public']['Tables']['event_match_schedule']['Row'];
type MatchScheduleInsert = Database['public']['Tables']['event_match_schedule']['Insert'];
type MatchScheduleUpdate = Database['public']['Tables']['event_match_schedule']['Update'];

type Event = Database['public']['Tables']['event']['Row'];
type EventInsert = Database['public']['Tables']['event']['Insert'];
type EventUpdate = Database['public']['Tables']['event']['Update'];

const HOUR_MS = 60 * 60 * 1000;

/**
 * The scorer/active-event visibility window — the single source of truth for how
 * far ahead of (and behind) an event it becomes scorable. Change these two
 * numbers to retune the window; nothing else hard-codes the hours.
 */
export const SCORER_WINDOW = {
  /** Event is visible this many hours BEFORE its start_date. */
  beforeStartHours: 24,
  /** Event stays visible this many hours AFTER its end_date. */
  afterEndHours: 12,
} as const;

/**
 * The live-schedule row projection. `getLiveScheduleInitialData` (the whole board) and
 * `getLiveScheduleRowsByIds` (the rows a Mission Control move changed) both send exactly this,
 * so a merged row has the initial row's shape.
 */
const LIVE_SCHEDULE_ROW_SELECT = `
        id,
        updated_at,
        event_id,
        scheduled_datetime,
        court_id,
        status,
        estimated_duration_minutes,
        best_of,
        points_per_game,
        deciding_game_points,
        notes,
        player_1_id,
        player_2_id,
        is_walkover,
        outcome,
        event_group_id,
        player1:player!event_match_schedule_player_1_id_fkey (
          id,
          first_name,
          last_name,
          username,
          alias,
          current_rating,
          profile_photo
        ),
        player2:player!event_match_schedule_player_2_id_fkey (
          id,
          first_name,
          last_name,
          username,
          alias,
          current_rating,
          profile_photo
        ),
        event:event_id (
          id,
          name,
          start_date,
          end_date,
          settings
        ),
        event_group:event_group_id (
          id,
          name
        ),
        match:match_id (
          id,
          best_of,
          points_per_game,
          deciding_game_points,
          deuce_rule,
          player_1_games,
          player_2_games,
          winner_id,
          retired_player_id,
          status,
          app_state,
          match_game (
            id,
            game_number,
            player_1_score,
            player_2_score,
            status,
            winner_id
          )
        ),
        draw_match:draw_match!event_match_schedule_id (
          id,
          winner_id,
          draw_match_id,
          stage_id,
          stage:stage_id (
            id,
            name
          ),
          draw:draw_id (
            id,
            draw_seeding!draw_seeding_draw_id_fkey (
              player_id,
              seeding_group,
              is_lucky_loser_slot
            )
          )
        )
      `;

export const eventModelV2 = {
  // Event Admin functions
  /**
   * What `POST /registrants/reset` will remove: the event's divisions, every
   * registration in them (any status) and the event's purchase transactions.
   * Head-only counts; nothing is fetched.
   */
  async countResetScope(
    client: SupabaseClient<Database>,
    eventId: string,
  ): Promise<{ divisions: number; registrations: number; transactions: number }> {
    const [groups, regs, transactions] = await Promise.all([
      client
        .from('event_group')
        .select('id', { count: 'exact', head: true })
        .eq('event_id', eventId),
      client
        .from('event_group_registration')
        .select('id, event_group!inner(event_id)', { count: 'exact', head: true })
        .eq('event_group.event_id', eventId),
      client
        .from('purchase_transaction')
        .select('id', { count: 'exact', head: true })
        .eq('event_id', eventId),
    ]);
    const failed = groups.error ?? regs.error ?? transactions.error;
    if (failed) {
      console.error('Error counting reset scope:', failed);
      throw new Error('Failed to count reset scope');
    }
    return {
      divisions: groups.count ?? 0,
      registrations: regs.count ?? 0,
      transactions: transactions.count ?? 0,
    };
  },

  /**
   * What `POST /registrants/reset-fixed-csv` will remove: divisions, registrations,
   * purchase transactions, and the guest players (no linked user) registered here
   * who have no registration in any other event. Guests are a distinct count over
   * player ids, so only `player_id` is read (paged), then checked against other events
   * in chunks, exactly as the POST does.
   */
  async countFixedCsvResetScope(
    client: SupabaseClient<Database>,
    eventId: string,
  ): Promise<{ divisions: number; registrations: number; transactions: number; guests: number }> {
    const base = await this.countResetScope(client, eventId);

    const PAGE = 1000;
    const candidates = new Set<string>();
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await client
        .from('event_group_registration')
        .select('player_id, player:player_id!inner(user_id), event_group!inner(event_id)')
        .eq('event_group.event_id', eventId)
        .is('player.user_id', null)
        .order('id')
        .range(from, from + PAGE - 1);
      if (error) {
        console.error('Error counting guest players:', error);
        throw new Error('Failed to count reset scope');
      }
      const page = data ?? [];
      for (const row of page) if (row.player_id) candidates.add(row.player_id);
      if (page.length < PAGE) break;
    }

    const ids = Array.from(candidates);
    const stillRegistered = new Set<string>();
    const CHUNK = 150;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const chunk = ids.slice(i, i + CHUNK);
      // A chunk's rows can exceed the server's row cap (a guest in many events), so page it.
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await client
          .from('event_group_registration')
          .select('player_id, event_group!inner(event_id)')
          .neq('event_group.event_id', eventId)
          .in('player_id', chunk)
          .order('id')
          .range(from, from + PAGE - 1);
        if (error) {
          console.error('Error checking guest registrations elsewhere:', error);
          throw new Error('Failed to count reset scope');
        }
        const page = data ?? [];
        for (const row of page) if (row.player_id) stillRegistered.add(row.player_id);
        if (page.length < PAGE) break;
      }
    }

    return {
      divisions: base.divisions,
      registrations: base.registrations,
      transactions: base.transactions,
      guests: ids.filter((id) => !stillRegistered.has(id)).length,
    };
  },

  async getEventAdmins(client: SupabaseClient<Database>, eventId: string) {
    const { resolveAdminDisplayInfo } = await import('@/lib/invite-helpers');

    const { data: admins, error: adminError } = await client
      .from('event_admin')
      .select('id, user_id, role, status')
      .eq('event_id', eventId)
      .eq('status', 'active');

    if (adminError) throw adminError;
    if (!admins?.length) return [];

    const userIds = admins.map((admin) => admin.user_id).filter(Boolean);
    const resolved = await resolveAdminDisplayInfo(client, userIds);

    // Attach role from event_admin record
    return resolved.map((item) => {
      const admin = admins.find((a) => a.user_id === item.user_id);
      return { ...item, role: admin?.role || 'admin' };
    });
  },

  async addEventAdmin(
    client: SupabaseClient<Database>,
    eventId: string,
    userId: string,
    role: string = 'admin',
  ): Promise<void> {
    const { error } = await client.from('event_admin').insert({
      event_id: eventId,
      user_id: userId,
      role,
      status: 'active',
    });

    if (error) throw error;
  },

  async removeEventAdmin(
    client: SupabaseClient<Database>,
    eventId: string,
    userId: string,
  ): Promise<void> {
    const { error } = await client
      .from('event_admin')
      .delete()
      .eq('event_id', eventId)
      .eq('user_id', userId);

    if (error) throw error;
  },

  /**
   * Lightweight event fetch — only essential fields, no settings blob.
   * Use for high-frequency callers (permission checks, live-schedule polling)
   * where the full settings payload is not needed.
   */
  async getEventLight(
    client: SupabaseClient<Database>,
    eventId: string,
  ): Promise<Pick<
    Event,
    'id' | 'name' | 'federation_id' | 'status' | 'start_date' | 'end_date'
  > | null> {
    const { data, error } = await client
      .from('event')
      .select('id, name, federation_id, status, start_date, end_date')
      .eq('id', eventId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  // Basic event functions
  async getEvent(eventId: string): Promise<Event | null> {
    const client = createClient();
    if (!client) throw new Error('Client not available');

    const { data, error } = await client.from('event').select('*').eq('id', eventId).single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No rows returned
      throw error;
    }
    return data;
  },

  // Method for PermissionAwareModel compatibility
  async get(client: SupabaseClient<Database>, eventId: string): Promise<Event | null> {
    const { data, error } = await client.from('event').select('*').eq('id', eventId).single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No rows returned
      throw error;
    }
    return data;
  },

  async getEvents(federationId?: string, status?: string, eventType?: string): Promise<Event[]> {
    const adminClient = getSupabaseAdminClient();
    if (!adminClient) throw new Error('Admin client not available');

    let query = adminClient.from('event').select('*').order('start_date', { ascending: true });

    if (federationId) {
      query = query.eq('federation_id', federationId);
    }

    if (status) {
      query = query.eq('status', status);
    }

    if (eventType) {
      query = query.eq('event_type', eventType);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },

  async listByFederationIds(
    client: SupabaseClient<Database>,
    federationIds: string[],
    options?: { event_type?: string },
  ): Promise<Event[]> {
    if (!federationIds.length) return [];

    let query = client
      .from('event')
      .select('*')
      .in('federation_id', federationIds)
      .order('start_date', { ascending: true });

    if (options?.event_type) {
      query = query.eq('event_type', options.event_type);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },

  async listByLocationId(
    client: SupabaseClient<Database>,
    locationId: string,
    options?: { event_type?: string },
  ): Promise<Event[]> {
    let query = client.from('event').select('*').eq('location_id', locationId);
    if (options?.event_type) {
      query = query.eq('event_type', options.event_type);
    }
    const { data, error } = await query.order('start_date', { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  async listByIds(
    client: SupabaseClient<Database>,
    eventIds: string[],
    options?: { event_type?: string },
  ): Promise<Event[]> {
    if (!eventIds.length) return [];

    let query = client
      .from('event')
      .select('*')
      .in('id', eventIds)
      .order('start_date', { ascending: true });

    if (options?.event_type) {
      query = query.eq('event_type', options.event_type);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },

  async createEvent(
    eventData: EventInsert,
    scope?: { federationId: string; locationId: string },
  ): Promise<Event> {
    const adminClient = getSupabaseAdminClient();
    if (!adminClient) throw new Error('Admin client not available');

    let payload = eventData;
    if (scope) {
      // Forge-hardening (club path): assert the location belongs to the scoped
      // federation, then inject server-trusted ids, discarding any client-supplied
      // federation_id/location_id. locationServerModel.get throws on not-found
      // (uses .single()), so no null-guard is needed.
      const location = await locationServerModel.get(adminClient, scope.locationId);
      if (location.federation_id !== scope.federationId) {
        throw new Error('Location does not belong to the scoped federation');
      }
      payload = {
        ...eventData,
        federation_id: scope.federationId,
        location_id: scope.locationId,
        // The club runs the event it creates (Members phase 5). Server-set like the ids above, so a
        // client-supplied owner is discarded; the admin path names none and the insert trigger
        // defaults it to the federation.
        owner_kind: 'club',
        owner_id: scope.locationId,
      };
    }

    const { data, error } = await adminClient.from('event').insert(payload).select().single();

    if (error) throw error;
    return data;
  },

  async updateEvent(eventId: string, eventData: EventUpdate): Promise<Event> {
    const adminClient = getSupabaseAdminClient();
    if (!adminClient) throw new Error('Admin client not available');

    const { data, error } = await adminClient
      .from('event')
      .update(eventData)
      .eq('id', eventId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteEvent(eventId: string): Promise<void> {
    const adminClient = getSupabaseAdminClient();
    if (!adminClient) throw new Error('Admin client not available');

    const { error } = await adminClient.from('event').delete().eq('id', eventId);

    if (error) throw error;
  },

  /** The fields a billing write needs to guard on, or null when the row is not in this event. */
  async getTransactionForEvent(transactionId: string, eventId: string) {
    const adminClient = getSupabaseAdminClient();
    if (!adminClient) throw new Error('Admin client not available');
    const { data, error } = await adminClient
      .from('purchase_transaction')
      .select('id, event_id, status, payment_method, amount, refunded_amount, updated_at')
      .eq('id', transactionId)
      .eq('event_id', eventId)
      .maybeSingle();
    if (error) throw new Error('Failed to read transaction');
    return data;
  },

  /**
   * Update a purchase_transaction by ID.
   * Returns the updated transaction with player info.
   *
   * With `expectedUpdatedAt` the write is conditional: it only lands if the row's `updated_at`
   * still equals the value the caller read, and `null` is returned when nothing matched (the row
   * changed or went away in between). Without it, a missing row throws as before.
   */
  async updateTransaction(
    transactionId: string,
    updates: Database['public']['Tables']['purchase_transaction']['Update'],
    eventId?: string,
    expectedUpdatedAt?: string,
  ): Promise<
    | (Database['public']['Tables']['purchase_transaction']['Row'] & {
        player: Pick<
          Database['public']['Tables']['player']['Row'],
          'id' | 'username' | 'first_name' | 'last_name' | 'current_rating' | 'created_at'
        > | null;
      })
    | null
  > {
    const adminClient = getSupabaseAdminClient();
    if (!adminClient) throw new Error('Admin client not available');

    let query = adminClient.from('purchase_transaction').update(updates).eq('id', transactionId);
    // Scope to the event the caller was authorised for.
    if (eventId) query = query.eq('event_id', eventId);
    if (expectedUpdatedAt) query = query.eq('updated_at', expectedUpdatedAt);
    const { data: updated, error: updateError } = await query
      .select(
        `
        *,
        player:player_id (
          id,
          username,
          first_name,
          last_name,
          current_rating,
          created_at
        )
      `,
      )
      .maybeSingle();

    if (updateError) {
      console.error('Error updating transaction:', updateError);
      throw new Error('Failed to update transaction');
    }

    if (!updated) {
      if (expectedUpdatedAt) return null;
      throw new Error('Transaction not found');
    }

    return updated as typeof updated & {
      player: Pick<
        Database['public']['Tables']['player']['Row'],
        'id' | 'username' | 'first_name' | 'last_name' | 'current_rating' | 'created_at'
      > | null;
    };
  },

  // Match Schedule functions
  /**
   * Get match schedules for an event with full player and group information
   * @param eventId - The event ID
   * @param isAdmin - Whether to use admin client (for admin access)
   * @returns Array of match schedules with player and group data
   */
  async getMatchSchedulesForEvent(eventId: string, isAdmin: boolean = false) {
    const client = isAdmin ? getSupabaseAdminClient() : createClient();

    if (!client) {
      throw new Error('Supabase client not configured');
    }

    const { data: matchSchedules, error } = await client
      .from('event_match_schedule')
      .select(
        `
        *,
        event_group:event_group_id (
          id,
          name,
          group_type,
          sort_order
        ),
        player_1:player!event_match_schedule_player_1_id_fkey (
          id,
          first_name,
          last_name,
          username,
          current_rating,
          profile_photo
        ),
        player_2:player!event_match_schedule_player_2_id_fkey (
          id,
          first_name,
          last_name,
          username,
          current_rating,
          profile_photo
        ),
        match:match_id (
          id,
          best_of,
          points_per_game,
          deciding_game_points,
          deuce_rule,
          status,
          winner_id,
          app_state
        )
      `,
      )
      .eq('event_id', eventId)
      .order('scheduled_datetime', { ascending: true });

    if (error) {
      console.error('Error fetching match schedules:', error);
      throw new Error('Failed to fetch match schedules');
    }

    return matchSchedules || [];
  },

  /**
   * Get match schedules for a public event page, including nested rating and game score data.
   * Single query — avoids the N+1 pattern of fetching ratings/scores per completed match.
   *
   * Also returns the event's stored live topology revision (`event_live_scores`), embedded
   * in the same statement so it is read from the same snapshot as the rows: the rows are
   * exactly as new as the revision reported with them. 0 when there are no rows or the
   * event has no visible live row (RLS), so a nonzero live revision still reloads.
   */
  async getPublicMatchSchedulesForEvent(eventId: string) {
    const client = createClient();

    if (!client) {
      throw new Error('Supabase client not configured');
    }

    let matchSchedules;
    try {
      // Paged: PostgREST caps one read at max_rows (1,000), so an event past that lost its
      // tail silently. `id` is the unique tiebreak that keeps pages from repeating or skipping.
      matchSchedules = await selectAllRows((from, to) =>
        client
          .from('event_match_schedule')
          .select(
            `
            *,
            event_group:event_group_id (
              id,
              name,
              group_type,
              sort_order,
              is_team
            ),
            player_1:player!event_match_schedule_player_1_id_fkey (
              id,
              first_name,
              last_name,
              alias,
              username,
              current_rating
            ),
            player_2:player!event_match_schedule_player_2_id_fkey (
              id,
              first_name,
              last_name,
              alias,
              username,
              current_rating
            ),
            match:match_id (
              id,
              status,
              winner_id,
              app_state,
              rating (
                player_id,
                rating_before,
                rating_after,
                k_factor
              ),
              match_game (
                game_number,
                player_1_score,
                player_2_score,
                status
              )
            ),
            draw_match:draw_match!event_match_schedule_id (
              draw_match_id,
              round:round_id ( name ),
              draw:draw_id (
                hidden_from_public,
                draw_seeding!draw_seeding_draw_id_fkey (
                  player_id,
                  is_lucky_loser_slot
                )
              )
            ),
            live:event_id (
              event_live_scores (
                topology_revision
              )
            )
          `,
          )
          .eq('event_id', eventId)
          .order('scheduled_datetime', { ascending: true })
          .order('id', { ascending: true })
          .range(from, to),
      );
    } catch (error) {
      console.error('Error fetching public match schedules:', error);
      throw new Error('Failed to fetch match schedules');
    }

    const rows = (matchSchedules || []).map(({ live, ...row }) => ({
      row,
      topologyRevision: Number(live?.event_live_scores?.topology_revision ?? 0),
    }));
    const schedules = await gateRowAliases(
      // A hidden draw takes its matches off the public read (the bracket is gated separately).
      dropHiddenDrawRows(rows.map(({ row }) => row)),
      ['player_1', 'player_2'],
      () => isEventAliasAllowed(eventId, client),
    );
    return { schedules, topologyRevision: rows[0]?.topologyRevision ?? 0 };
  },

  /**
   * Create a new match schedule
   * @param matchScheduleData - The match schedule data
   * @param isAdmin - Whether to use admin client
   * @returns The created match schedule
   */
  async createMatchSchedule(
    matchScheduleData: Omit<MatchScheduleInsert, 'id' | 'created_at' | 'updated_at'>,
    isAdmin: boolean = false,
  ) {
    const client = isAdmin ? getSupabaseAdminClient() : createClient();

    if (!client) {
      throw new Error('Supabase client not configured');
    }

    const { data: matchSchedule, error } = await client
      .from('event_match_schedule')
      .insert({
        ...matchScheduleData,
        status: matchScheduleData.status || 'scheduled',
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating match schedule:', error);
      throw new Error('Failed to create match schedule');
    }

    return matchSchedule;
  },

  /**
   * Update a match schedule
   * @param scheduleId - The schedule ID
   * @param updates - The updates to apply
   * @param isAdmin - Whether to use admin client
   * @returns The updated match schedule
   */
  async updateMatchSchedule(
    scheduleId: string,
    updates: Omit<MatchScheduleUpdate, 'id' | 'created_at' | 'updated_at'>,
    isAdmin: boolean = false,
  ) {
    const client = isAdmin ? getSupabaseAdminClient() : createClient();

    if (!client) {
      throw new Error('Supabase client not configured');
    }

    // First check if the match schedule exists
    const { data: _existingSchedule, error: checkError } = await client
      .from('event_match_schedule')
      .select('id, event_id')
      .eq('id', scheduleId)
      .single();

    if (checkError) {
      console.error('Error checking match schedule existence:', checkError);
      throw new Error(`Match schedule ${scheduleId} not found: ${checkError.message}`);
    }

    const { data: matchSchedule, error } = await client
      .from('event_match_schedule')
      .update(updates)
      .eq('id', scheduleId)
      .select()
      .single();

    if (error) {
      console.error('Error updating match schedule:', error);
      throw new Error(`Failed to update match schedule ${scheduleId}: ${error.message}`);
    }

    return matchSchedule;
  },

  /**
   * Claim an unlinked schedule only while canonical and seeded participant identities
   * still match its locked current seats. Shares the atomic advancement draw fence.
   * Returns true if this call won the link, false if another concurrent start
   * already linked it. Guards the start-from-schedule race so two near-
   * simultaneous starts can't both link and leave an orphaned match. Uses the
   * admin client (the link is part of the start contract, mirroring
   * updateMatchSchedule's admin write).
   */
  async linkScheduleToMatchIfUnlinked(scheduleId: string, matchId: string): Promise<boolean> {
    const admin = getSupabaseAdminClient();
    if (!admin) {
      throw new Error('Supabase client not configured');
    }

    const { data, error } = await admin.rpc('scorer_link_scheduled_match', {
      p_schedule_id: scheduleId,
      p_match_id: matchId,
    });

    if (error) {
      console.error('Error linking schedule to match:', error);
      throw Object.assign(
        new Error(`Failed to link schedule ${scheduleId} to match ${matchId}: ${error.message}`),
        { code: error.code },
      );
    }

    return data === true;
  },

  /**
   * Delete a match schedule
   * @param scheduleId - The schedule ID
   * @param isAdmin - Whether to use admin client
   */
  async deleteMatchSchedule(scheduleId: string, isAdmin: boolean = false) {
    const client = isAdmin ? getSupabaseAdminClient() : createClient();

    if (!client) {
      throw new Error('Supabase client not configured');
    }

    const { error } = await client.from('event_match_schedule').delete().eq('id', scheduleId);

    if (error) {
      console.error('Error deleting match schedule:', error);
      throw new Error('Failed to delete match schedule');
    }
  },

  /**
   * Get events that a player can access based on participation and admin permissions.
   * Restricted to the {@link SCORER_WINDOW} around each event's start/end, and
   * to PUBLISHED events: a draft or cancelled event can be neither scored
   * (from-schedule refuses it) nor streamed (`can_stream_at_location` refuses
   * it), so listing one only leads to a dead end (2026-10-20: a draft event
   * reached the app's Go live screen and its channels never loaded).
   * @param playerId - The player ID
   * @param federationIds - Array of federation IDs where user is admin
   * @param eventIds - Array of event IDs where user is admin/referee
   * @param supabaseClient - Optional Supabase client
   * @returns Array of events the player can access
   */
  async getEventsByPlayerWithPermissions(
    playerId: string,
    federationIds: string[],
    eventIds: string[],
    supabaseClient?: SupabaseClient<Database> | null,
  ): Promise<Event[]> {
    const client = supabaseClient || createClient();
    if (!client) throw new Error('Supabase client not configured');

    const now = new Date();
    const visibleBeforeStart = new Date(now.getTime() + SCORER_WINDOW.beforeStartHours * HOUR_MS);
    const visibleAfterEnd = new Date(now.getTime() - SCORER_WINDOW.afterEndHours * HOUR_MS);

    // Get events where player is registered (via event_group_registration -> event_group -> event)
    const { data: groupRegs, error: registeredError } = await client
      .from('event_group_registration')
      .select('event_group:event_group_id (event:event_id (*))')
      .eq('player_id', playerId);

    if (registeredError) throw registeredError;

    // Flatten to event list and apply time window filter
    const registeredEvents = (groupRegs || [])
      .map((r) => {
        const eg = r.event_group as unknown as { event: Event | null } | null;
        return eg?.event ?? null;
      })
      .filter(
        (e): e is Event =>
          e !== null &&
          new Date(e.start_date) <= visibleBeforeStart &&
          new Date(e.end_date) >= visibleAfterEnd,
      );

    // Gate the *registered* branch by the "allow registered players to ref"
    // setting (per-event override on top of the federation default). Events the
    // player can still reach via federation-admin / admin / referee roles below
    // are unaffected — this only governs access earned purely by registering.
    const registeredFederationIds = [
      ...new Set(
        registeredEvents.map((e) => e.federation_id).filter((id): id is string => Boolean(id)),
      ),
    ];
    const federationAllowsById = new Map<string, boolean | null>();
    if (registeredFederationIds.length > 0) {
      const { data: federationFlags, error: federationFlagsError } = await client
        .from('federation')
        .select('id, allow_registered_players_to_ref')
        .in('id', registeredFederationIds);

      if (federationFlagsError) throw federationFlagsError;
      for (const f of federationFlags || []) {
        federationAllowsById.set(f.id, f.allow_registered_players_to_ref);
      }
    }
    const refEligibleRegisteredEvents = filterRegisteredEventsRefEligible(
      registeredEvents,
      federationAllowsById,
    );

    // Get events from federations where user is admin
    const { data: federationEvents, error: federationError } = await client
      .from('event')
      .select('*')
      .in('federation_id', federationIds)
      .lte('start_date', visibleBeforeStart.toISOString())
      .gte('end_date', visibleAfterEnd.toISOString());

    if (federationError) throw federationError;

    // Get events where user is admin/referee
    const { data: adminEvents, error: adminError } = await client
      .from('event')
      .select('*')
      .in('id', eventIds)
      .lte('start_date', visibleBeforeStart.toISOString())
      .gte('end_date', visibleAfterEnd.toISOString());

    if (adminError) throw adminError;

    // Combine all events and remove duplicates
    const allEvents: Event[] = [];
    const eventIdsSeen = new Set<string>();

    // Add registered events (gated by ref eligibility)
    for (const event of refEligibleRegisteredEvents) {
      if (!eventIdsSeen.has(event.id)) {
        allEvents.push(event);
        eventIdsSeen.add(event.id);
      }
    }

    // Add federation events
    for (const event of federationEvents || []) {
      if (!eventIdsSeen.has(event.id)) {
        allEvents.push(event);
        eventIdsSeen.add(event.id);
      }
    }

    // Add admin events
    for (const event of adminEvents || []) {
      if (!eventIdsSeen.has(event.id)) {
        allEvents.push(event);
        eventIdsSeen.add(event.id);
      }
    }

    // Only published events, from every branch (see the doc comment). Filtered
    // here rather than per query so the registered branch's embedded event,
    // which PostgREST cannot filter on the parent row, obeys the same rule.
    return allEvents
      .filter((e) => e.status === 'published')
      .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
  },

  /**
   * Create a new match schedule with event ID
   * @param eventId - The event ID
   * @param data - The match schedule data
   * @param isAdmin - Whether to use admin client
   * @returns The created match schedule
   */
  async createMatchScheduleWithEventId(
    eventId: string,
    data: Omit<MatchScheduleInsert, 'id' | 'created_at' | 'updated_at'>,
    isAdmin: boolean = false,
  ): Promise<MatchSchedule> {
    const client = isAdmin ? getSupabaseAdminClient() : createClient();

    if (!client) {
      throw new Error('Supabase client not configured');
    }

    const { data: matchSchedule, error } = await client
      .from('event_match_schedule')
      .insert({
        ...data,
        event_id: eventId,
        status: data.status || 'scheduled',
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create match schedule: ${error.message}`);
    }

    return matchSchedule;
  },

  /**
   * Update an existing match schedule with event ID validation
   * @param eventId - The event ID
   * @param scheduleId - The schedule ID
   * @param data - The update data
   * @param isAdmin - Whether to use admin client
   * @returns The updated match schedule
   */
  async updateMatchScheduleWithEventId(
    eventId: string,
    scheduleId: string,
    data: Omit<MatchScheduleUpdate, 'id' | 'created_at' | 'updated_at'>,
    isAdmin: boolean = false,
  ): Promise<MatchSchedule> {
    const client = isAdmin ? getSupabaseAdminClient() : createClient();

    if (!client) {
      throw new Error('Supabase client not configured');
    }

    const { data: matchSchedule, error } = await client
      .from('event_match_schedule')
      .update(data)
      .eq('id', scheduleId)
      .eq('event_id', eventId) // Ensure we only update matches for this event
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update match schedule ${scheduleId}: ${error.message}`);
    }

    return matchSchedule;
  },

  /**
   * Played state of many schedules at once, as `id -> played` (completed, has a winner, or in
   * progress). A missing row is simply absent from the map: it reads as not played, so deleting
   * it stays an idempotent no-op. Chunked: a long `.in()` list silently 414s.
   */
  async getMatchSchedulePlayedMap(
    eventId: string,
    scheduleIds: string[],
    isAdmin: boolean = false,
  ): Promise<Map<string, boolean>> {
    const client = isAdmin ? getSupabaseAdminClient() : createClient();

    if (!client) {
      throw new Error('Supabase client not configured');
    }

    const played = new Map<string, boolean>();
    const CHUNK = 150;
    for (let i = 0; i < scheduleIds.length; i += CHUNK) {
      const { data, error } = await client
        .from('event_match_schedule')
        .select('id, status, match:match_id(status, winner_id)')
        .eq('event_id', eventId)
        .in('id', scheduleIds.slice(i, i + CHUNK));

      if (error) {
        throw new Error(`Failed to read match schedules: ${error.message}`);
      }
      for (const row of data ?? []) played.set(row.id, isPlayedSchedule(row));
    }
    return played;
  },

  /**
   * Delete a match schedule with event ID validation
   * @param eventId - The event ID
   * @param scheduleId - The schedule ID
   * @param isAdmin - Whether to use admin client
   * @returns Success status
   */
  async deleteMatchScheduleWithEventId(
    eventId: string,
    scheduleId: string,
    isAdmin: boolean = false,
  ): Promise<boolean> {
    const client = isAdmin ? getSupabaseAdminClient() : createClient();

    if (!client) {
      throw new Error('Supabase client not configured');
    }

    const { error } = await client
      .from('event_match_schedule')
      .delete()
      .eq('id', scheduleId)
      .eq('event_id', eventId); // Ensure we only delete matches for this event

    if (error) {
      throw new Error(`Failed to delete match schedule ${scheduleId}: ${error.message}`);
    }

    return true;
  },

  /**
   * Batch update match schedules with atomic operations
   * @param eventId - The event ID
   * @param operations - Array of operations to perform
   * @param isAdmin - Whether to use admin client
   * @returns The results of the batch operation
   */
  async batchUpdateMatchSchedules(
    eventId: string,
    operations: Array<{
      type: 'create' | 'update' | 'delete';
      id?: string;
      data?: Omit<MatchScheduleInsert, 'id' | 'created_at' | 'updated_at'>;
      draw_match_id?: string | null; // For linking back to draw match
    }>,
    isAdmin: boolean = false,
  ): Promise<{
    success: boolean;
    results: Array<{
      operation: string;
      id?: string;
      success: boolean;
      error?: string;
      data?: MatchSchedule;
      draw_match_id?: string | null;
    }>;
    errors: string[];
  }> {
    const results: Array<{
      operation: string;
      id?: string;
      success: boolean;
      error?: string;
      data?: MatchSchedule;
      draw_match_id?: string | null;
    }> = [];
    const errors: string[] = [];

    // One played-state prefetch for every delete in the call. If it fails, each delete fails
    // per row (never match_played, never silently allowed) rather than deleting blind.
    let playedById: Map<string, boolean> | null = null;
    let playedLookupError: Error | null = null;
    const deleteIds = operations.flatMap((o) => (o.type === 'delete' && o.id ? [o.id] : []));
    if (deleteIds.length > 0) {
      try {
        playedById = await this.getMatchSchedulePlayedMap(eventId, deleteIds, isAdmin);
      } catch (e) {
        playedLookupError = e instanceof Error ? e : new Error('Failed to read match schedules');
      }
    }

    // Process operations in batches to avoid overwhelming the database
    const batchSize = 50;
    for (let i = 0; i < operations.length; i += batchSize) {
      const batch = operations.slice(i, i + batchSize);

      for (const operation of batch) {
        try {
          if (operation.type === 'create' && operation.data) {
            if (operation.draw_match_id) {
              // Use atomic RPC to create/update schedule and link draw_match in one transaction
              const admin = getSupabaseAdminClient()!;
              const { data: rpcRaw, error: rpcError } = await admin.rpc('upsert_match_schedule', {
                p_event_id: eventId,
                p_draw_match_id: operation.draw_match_id,
                p_data: operation.data,
              });

              if (rpcError) {
                throw new Error(`RPC error: ${rpcError.message}`);
              }

              const rpcResult = rpcRaw as unknown as {
                success: boolean;
                operation: string;
                schedule_id: string;
                data: MatchSchedule;
                error?: string;
              };

              if (!rpcResult?.success) {
                throw new Error(rpcResult?.error || 'upsert_match_schedule failed');
              }

              results.push({
                operation: rpcResult.operation,
                id: rpcResult.schedule_id,
                success: true,
                data: rpcResult.data,
                draw_match_id: operation.draw_match_id,
              });
            } else {
              // No draw_match_id: use existing create path
              const matchSchedule = await this.createMatchScheduleWithEventId(
                eventId,
                operation.data,
                isAdmin,
              );

              results.push({
                operation: 'create',
                id: matchSchedule.id,
                success: true,
                data: matchSchedule,
                draw_match_id: operation.draw_match_id,
              });
            }
          } else if (operation.type === 'update' && operation.id && operation.data) {
            const matchSchedule = await this.updateMatchScheduleWithEventId(
              eventId,
              operation.id,
              operation.data,
              isAdmin,
            );
            results.push({
              operation: 'update',
              id: operation.id,
              success: true,
              data: matchSchedule,
              draw_match_id: operation.draw_match_id,
            });
          } else if (operation.type === 'delete' && operation.id) {
            // Played matches are never deleted: refuse per row (one result per op, in order) so the
            // client can restore the row instead of treating the whole batch as failed.
            if (playedLookupError) throw playedLookupError;
            if (playedById?.get(operation.id)) {
              results.push({
                operation: 'delete',
                id: operation.id,
                success: false,
                error: 'match_played',
                draw_match_id: operation.draw_match_id,
              });
              errors.push('delete operation failed: match_played');
              continue;
            }
            await this.deleteMatchScheduleWithEventId(eventId, operation.id, isAdmin);
            results.push({
              operation: 'delete',
              id: operation.id,
              success: true,
              draw_match_id: operation.draw_match_id,
            });
          } else {
            const errorMsg = `Invalid operation: ${JSON.stringify(operation)}`;
            results.push({
              operation: operation.type,
              id: operation.id,
              success: false,
              error: errorMsg,
              draw_match_id: operation.draw_match_id,
            });
            errors.push(errorMsg);
          }
        } catch (operationError) {
          const errorMsg =
            operationError instanceof Error ? operationError.message : 'Unknown error';
          results.push({
            operation: operation.type,
            id: operation.id,
            success: false,
            error: errorMsg,
            draw_match_id: operation.draw_match_id,
          });
          errors.push(`${operation.type} operation failed: ${errorMsg}`);
        }
      }
    }

    const successCount = results.filter((r) => r.success).length;
    const totalCount = results.length;

    return {
      success: successCount === totalCount,
      results,
      errors,
    };
  },

  /**
   * Get live schedule initial data with full relations for admin live schedule view
   * @param client - Supabase client
   * @param eventId - The event ID
   * @returns Array of match schedules with full player, match, and draw data
   */
  async getLiveScheduleInitialData(client: SupabaseClient<Database>, eventId: string) {
    let matchSchedules;
    try {
      // Paged: PostgREST caps one read at max_rows (1,000). `id` is the unique tiebreak that keeps
      // pages from repeating or skipping rows.
      matchSchedules = await selectAllRows((from, to) =>
        client
          .from('event_match_schedule')
          .select(LIVE_SCHEDULE_ROW_SELECT)
          .eq('event_id', eventId)
          .order('scheduled_datetime', { ascending: true })
          .order('id', { ascending: true })
          .range(from, to),
      );
    } catch (error) {
      console.error('Error fetching live schedule initial data:', error);
      throw new Error('Failed to fetch live schedule initial data');
    }

    return gateRowAliases(matchSchedules, ['player1', 'player2'], () =>
      isEventAliasAllowed(eventId, client),
    );
  },

  /**
   * The given schedule rows of an event, in exactly the `getLiveScheduleInitialData` row shape
   * (same projection). Mission Control re-reads the one row a move changed with this instead of
   * the whole board. Ids from another event return nothing.
   * @param client - Supabase client
   * @param eventId - The event ID
   * @param scheduleIds - Schedule ids (the route caps them at 50)
   */
  async getLiveScheduleRowsByIds(
    client: SupabaseClient<Database>,
    eventId: string,
    scheduleIds: string[],
  ) {
    if (scheduleIds.length === 0) return [];

    const { data: matchSchedules, error } = await client
      .from('event_match_schedule')
      .select(LIVE_SCHEDULE_ROW_SELECT)
      .eq('event_id', eventId)
      .in('id', scheduleIds)
      .order('scheduled_datetime', { ascending: true });

    if (error) {
      console.error('Error fetching live schedule rows:', error);
      throw new Error('Failed to fetch live schedule rows');
    }

    return gateRowAliases(matchSchedules || [], ['player1', 'player2'], () =>
      isEventAliasAllowed(eventId, client),
    );
  },

  /**
   * Get live schedule status changes since last check for admin live schedule view
   * @param client - Supabase client
   * @param eventId - The event ID
   * @param lastCheckDate - ISO string of last check timestamp
   * @returns Array of status changes with match data
   */
  async getLiveScheduleStatusChanges(
    client: SupabaseClient<Database>,
    eventId: string,
    lastCheckDate: string,
  ) {
    const select = `
        id,
        status,
        updated_at,
        scheduled_datetime,
        court_id,
        player_1_id,
        player_2_id,
        is_walkover,
        outcome,
        player1:player!event_match_schedule_player_1_id_fkey (
          id,
          first_name,
          last_name,
          username,
          alias
        ),
        player2:player!event_match_schedule_player_2_id_fkey (
          id,
          first_name,
          last_name,
          username,
          alias
        ),
        event_group:event_group_id (
          id,
          name
        ),
        match:match_id (
          id,
          best_of,
          points_per_game,
          deciding_game_points,
          deuce_rule,
          player_1_games,
          player_2_games,
          winner_id,
          retired_player_id,
          status,
          app_state,
          match_game (
            id,
            game_number,
            player_1_score,
            player_2_score,
            status,
            winner_id
          )
        ),
        draw_match:draw_match!event_match_schedule_id (
          id,
          winner_id,
          draw_match_id,
          stage_id,
          stage:stage_id (
            id,
            name
          )
        )
      `;

    let statusChanges;
    try {
      statusChanges = await selectAllRows(
        (from, to) =>
          client
            .from('event_match_schedule')
            .select(select)
            .eq('event_id', eventId)
            .gte('updated_at', lastCheckDate)
            .order('updated_at', { ascending: false })
            .order('id', { ascending: true })
            .range(from, to),
        undefined,
        // Polled every 15-60 s and normally a handful of rows: skip the confirming empty read.
        { stopOnShortPage: true },
      );
    } catch (error) {
      console.error('Error fetching live schedule status changes:', error);
      throw new Error('Failed to fetch live schedule status changes');
    }

    return gateRowAliases(statusChanges, ['player1', 'player2'], () =>
      isEventAliasAllowed(eventId, client),
    );
  },

  /**
   * Get live scores for specific match schedules for admin live schedule view
   * @param client - Supabase client
   * @param eventId - The event ID
   * @param matchIds - Array of match schedule IDs to get live scores for
   * @returns Array of match schedules with match data for live scoring
   */
  async getLiveScheduleLiveScores(
    client: SupabaseClient<Database>,
    eventId: string,
    matchIds: string[],
  ) {
    if (matchIds.length === 0) {
      return [];
    }

    const { data: liveScores, error } = await client
      .from('event_match_schedule')
      .select(
        `
        id,
        status,
        match:match_id (
          id,
          best_of,
          points_per_game,
          deciding_game_points,
          deuce_rule,
          player_1_games,
          player_2_games,
          winner_id,
          retired_player_id,
          status,
          app_state,
          match_game (
            id,
            game_number,
            player_1_score,
            player_2_score,
            status,
            winner_id
          )
        )
      `,
      )
      .eq('event_id', eventId)
      .in('id', matchIds);

    if (error) {
      console.error('Error fetching live scores:', error);
      throw new Error('Failed to fetch live scores');
    }

    return liveScores || [];
  },

  /**
   * Resolve the federation a match belongs to via its event-schedule link.
   * The `match` table has no event/federation column, so the only reliable
   * association is through `event_match_schedule`. Returns null for matches not
   * attached to a scheduled event (e.g. pickup games).
   */
  async getFederationIdByMatchId(
    client: SupabaseClient<Database>,
    matchId: string,
  ): Promise<string | null> {
    const { data, error } = await client
      .from('event_match_schedule')
      .select('event:event_id ( federation_id )')
      .eq('match_id', matchId)
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(`Failed to resolve federation for match: ${error.message}`);
    const ev = data?.event as { federation_id?: string | null } | null;
    return ev?.federation_id ?? null;
  },

  /**
   * Return the events the caller can act on right now: those within the
   * {@link SCORER_WINDOW} for which they have scorer-scope permissions (registered-ref-
   * eligible, federation-admin, or event-admin/referee). This is the canonical
   * "active events" definition shared across scoring and streaming surfaces.
   *
   * Equivalent to what `/api/app/events/scorable` returned before this was
   * extracted — that route now delegates here instead of composing the two
   * steps inline.
   */
  async getActiveEvents(
    supabase: SupabaseClient<Database>,
    userId: string,
    playerId: string,
  ): Promise<Event[]> {
    const { federationIds, eventIds } = await deriveScorerScopes(supabase, userId);
    return this.getEventsByPlayerWithPermissions(playerId, federationIds, eventIds, supabase);
  },

  /**
   * Public, narrow projection of the schedule for a published event.
   * - Strips ratings, profile photos, player IDs.
   * - Pre-computes per-court delayMin from the shared `courtDelayMinutes` rule
   *   (`@/lib/schedule/courtDelay`), only when the day viewed is today.
   * - Returns plain display names.
   * Throws when event is missing or not published.
   *
   * `tzOffsetMin` (minutes east of UTC) is the offset the caller renders times
   * in; when supplied (including 0) it moves the day boundary used for `days`,
   * `selectedDay` and the `day` filter. Omitted (the web schedule page; the
   * app since 2026-10-02), everything buckets by the calendar of the event's
   * resolved zone: pinned `event.timezone`, else the location's, else the default.
   */
  async getPublicSchedule(
    client: SupabaseClient<Database>,
    eventId: string,
    day?: string,
    tzOffsetMin?: number,
    /**
     * `ctx`: the event context the public pages already hold in cached loaders; with it the read is
     * one database wave. `exactDay`: return the asked day's rows even when that day has none,
     * instead of falling back (Watch, which filters to its own day).
     */
    opts: { ctx?: ScheduleContext | null; exactDay?: boolean } = {},
  ): Promise<{
    asOf: string;
    event: { id: string; name: string; start_date: string; end_date: string };
    /** The event location's IANA zone, or `DEFAULT_TIMEZONE` when it has none. */
    timezone: string;
    /** True when the organiser pinned `event.timezone` (the app renders in `timezone` only then). */
    timezonePinned: boolean;
    days: string[];
    selectedDay: string;
    courts: Array<{
      id: string;
      name: string;
      label?: string;
      delayMin: number;
      liveMatchId: string | null;
      /** `'primary'` for the event's own venue, an `event_venue` id otherwise; `null` only when the event has no venues. */
      venueId: string | null;
    }>;
    venues: Array<{ id: string; name: string; address: string | null; isPrimary: boolean }>;
    matches: Array<{
      id: string;
      matchId: string | null;
      courtId: string;
      scheduledAt: string;
      durationEstimateMin: number;
      stage: string;
      division: string | null;
      /** The organiser's division order (`event_group.sort_order`); null with no division. */
      divisionSortOrder: number | null;
      players: string[];
      playerIds?: (string | null)[];
      divisionId?: string | null;
      /** Team-division roster, verbatim, aligned index-for-index with `players`. */
      rosters?: (string | null)[];
      score: { p1: number; p2: number } | null;
      /** Current-game points while live (from the scorer blob); null otherwise. */
      liveGame: { p1: number; p2: number } | null;
      /**
       * Per-game points in play order — completed games from `match_game`, or
       * while live the resolved blob's games including the current one. Games
       * with no point yet are omitted. Empty when nothing has been scored.
       */
      games: { p1: number; p2: number; partial?: boolean }[];
      /** Draw match code ("QUAL-B2", "A7"); null when not from a draw. */
      code: string | null;
      /** Draw round name ("Quarter-final"); null without a draw match or a named round. */
      roundName: string | null;
      /** Seeding group per player ("2"), aligned with `players`; null when unseeded. */
      seeds: (string | null)[];
      isWalkover: boolean;
      outcome?: string | null;
      status: 'live' | 'upcoming' | 'completed';
      /** The linked match's winner (`match.winner_id`); null with no match or no winner yet. */
      winnerId?: string | null;
      /** The player who retired (`match.retired_player_id`); null unless outcome is 'retired'. */
      retiredPlayerId?: string | null;
    }>;
  }> {
    const asOf = new Date().toISOString();
    const { ctx = null, exactDay = false } = opts;

    // Without a context (the app schedule route, under the caller's own client) the event row is
    // the one read that must come first: the day window needs its zone.
    let ev: {
      id: string;
      name: string;
      status: string | null;
      start_date: string;
      end_date: string;
      settings: unknown;
      federation_id: string | null;
      timezone: string | null;
      location_id: string | null;
      locationZone: string | null | undefined;
    };
    if (ctx) {
      ev = { ...ctx.event, locationZone: ctx.event.location_timezone };
    } else {
      const { data: event, error: eventErr } = await client
        .from('event')
        .select(
          'id, name, status, start_date, end_date, settings, federation_id, timezone, location_id, location:location_id ( timezone )',
        )
        .eq('id', eventId)
        .maybeSingle();
      if (eventErr) throw new Error(`Failed to load event: ${eventErr.message}`);
      if (!event) throw new Error('Event not found');
      ev = {
        ...event,
        locationZone: (event as { location?: { timezone?: string | null } | null }).location
          ?.timezone,
      };
    }
    // The event's own pinned zone wins over its location's — the same coalesce the app_* RPCs
    // apply (migration 20261001120000).
    const pinnedZone = ev.timezone?.trim() ? ev.timezone : null;
    const timezone = normaliseTimezone(pinnedZone ?? ev.locationZone);
    if (ev.status !== 'published') throw new Error('Event is not published');

    const courtDefs =
      (
        ev.settings as {
          matchScheduler?: {
            courtDefinitions?: CourtDefinition[];
          };
        }
      )?.matchScheduler?.courtDefinitions || [];

    // Japanese federations order names Last+First (full-width space), detected via the
    // federation's default currency, as the rest of the app does. Name order is cosmetic: a failed
    // read renders First Last rather than failing the page, but it is reported, never silent.
    const readIsJapanese = async (): Promise<boolean> => {
      if (!ev.federation_id) return false;
      const { data: fed, error: fedErr } = await client
        .from('federation')
        .select('default_currency')
        .eq('id', ev.federation_id)
        .maybeSingle();
      if (fedErr)
        Sentry.captureException(new Error(`Failed to load federation: ${fedErr.message}`), {
          tags: { area: 'public-schedule', read: 'federation' },
        });
      return playerModelV2.isJapaneseFederation(fed?.default_currency ?? null);
    };

    // Day keys follow the caller's frame. An explicit `tzOffsetMin` (a caller that renders in its own
    // offset, older mobile builds) decides the day boundary, including 0 from a UTC-zone device. With
    // no offset (the web page; the app since 2026-10-02), bucket by the event's resolved zone, so the
    // day list agrees with the calendar every clock is shown in.
    const dayFromIso = (iso: string): string =>
      tzOffsetMin === undefined ? dayKeyInZone(iso, timezone) : dayKeyFor(iso, tzOffsetMin);
    const windowOf = (d: string) =>
      dayBoundsUtc(d, tzOffsetMin === undefined ? timezone : tzOffsetMin);
    const today = dayFromIso(new Date().toISOString());
    // `day` is user input (`?day=`): anything but a real `YYYY-MM-DD` is no day, as the whole-event
    // read treated it (it never matched a day, so the page showed today or the first day).
    const askedDay = isDayKey(day) ? day : undefined;
    // With no day asked, aim the first read at the day the fallback below usually picks: today
    // inside the event's dates, else its first day. Only which day is read first changes (the
    // selected day is still decided from the days list), so a read before or after the event is one
    // wave instead of two. `exactDay` keeps today: that caller filters to the day it aimed at.
    const startDay = String(ev.start_date ?? '').slice(0, 10);
    const endDay = String(ev.end_date ?? '').slice(0, 10);
    const inEvent =
      isDayKey(startDay) && today >= startDay && (!isDayKey(endDay) || today <= endDay);
    const aimedDay = askedDay ?? (exactDay || !isDayKey(startDay) || inEvent ? today : startDay);

    // One day's rows, read through a UTC window a little wider than the day (`dayBoundsUtc`); the
    // `dayFromIso` filter below stays the authority on which rows belong to the day. `match_game`
    // rides along: three small ints per game, unlike the ~20 KB `app_state` blob read separately.
    const readDayRows = (d: string) => {
      const { gte, lt } = windowOf(d);
      return client
        .from('event_match_schedule')
        .select(
          `
        id,
        court_id,
        scheduled_datetime,
        estimated_duration_minutes,
        is_walkover,
        outcome,
        player_1_id,
        player_2_id,
        player1:player!event_match_schedule_player_1_id_fkey ( first_name, last_name, alias ),
        player2:player!event_match_schedule_player_2_id_fkey ( first_name, last_name, alias ),
        event_group:event_group_id ( id, name, sort_order ),
        match:match_id ( id, status, winner_id, retired_player_id, player_1_games, player_2_games, match_game ( game_number, player_1_score, player_2_score, status ) ),
        draw_match:draw_match!event_match_schedule_id ( draw_match_id, draw_id, draw:draw_id ( hidden_from_public ), stage:stage_id ( name ), round:round_id ( name ) )
        `,
        )
        .eq('event_id', eventId)
        .gte('scheduled_datetime', gte)
        .lt('scheduled_datetime', lt)
        .order('scheduled_datetime', { ascending: true });
    };

    // Live scores come from the scorer blob, only for in-progress matches without a winner (a
    // handful on an event day, up to ~20 KB each): read for the day's window beside the rows,
    // never embedded on the rows. Decorative: log and fall back to the games columns.
    const readLiveBlobs = async (d: string): Promise<Map<string, unknown>> => {
      const { gte, lt } = windowOf(d);
      const { data, error } = await client
        .from('event_match_schedule')
        .select('match:match_id!inner ( id, app_state )')
        .eq('event_id', eventId)
        .gte('scheduled_datetime', gte)
        .lt('scheduled_datetime', lt)
        .eq('match.status', 'in_progress')
        .is('match.winner_id', null);
      if (error)
        console.error('Error loading live app_state for public schedule (non-fatal):', error);
      const blobs = new Map<string, unknown>();
      for (const r of (data ?? []) as { match?: { id: string; app_state: unknown } | null }[]) {
        if (r.match?.id) blobs.set(r.match.id, r.match.app_state);
      }
      return blobs;
    };

    // The days list needs every row's time, but nothing else: a thin index, with the draw's hidden
    // flag so a hidden draw's days stay off the list. Paged so a large event keeps every day (the
    // short-page stop saves the trailing empty read while the server's max_rows is 1000).
    const readDayIndex = () =>
      selectAllRows(
        (from, to) =>
          client
            .from('event_match_schedule')
            .select(
              'id, scheduled_datetime, draw_match:draw_match!event_match_schedule_id ( draw:draw_id ( hidden_from_public ) )',
            )
            .eq('event_id', eventId)
            .order('id', { ascending: true })
            .range(from, to),
        1000,
        { stopOnShortPage: true },
      ).catch((e: Error) => {
        throw new Error(`Failed to load schedule: ${e.message}`);
      });

    // Team entries, resolved once for the whole event (empty map for events without team divisions),
    // under the same client the caller passed in (the cached public schedule passes the service-role
    // client). Decorative: log and degrade to personal names.
    const readTeamEntries = async () => {
      const { data, error } = await client
        .from('event_group_registration')
        .select(
          `
        event_group_id,
        player_id,
        team_name,
        team_info,
        status,
        player:player_id ( first_name, last_name ),
        event_group:event_group_id!inner ( is_team, event_id )
        `,
        )
        .eq('event_group.event_id', eventId)
        .eq('event_group.is_team', true);
      if (error)
        console.error('Error loading team entries for public schedule (non-fatal):', error);
      return buildEventTeamEntryMap(
        (data || []) as unknown as Parameters<typeof buildEventTeamEntryMap>[0],
      );
    };

    // Seeding groups for the event's draws, keyed by draw and player (a draw's seeding is shared by
    // every match in it). Event-wide so it joins the wave; paged by id so a large event keeps every
    // seed (a short page ends the read: complete while PostgREST `max_rows` is at least 1000, the
    // Supabase default). Decorative: log and show no seeds on failure.
    const readSeeding = async () => {
      const seeds = new Map<string, string>();
      try {
        const rows = await selectAllRows(
          (from, to) =>
            client
              .from('draw_seeding')
              .select('draw_id, player_id, seeding_group, draw:draw_id!inner ( event_id )')
              .eq('draw.event_id', eventId)
              .not('seeding_group', 'is', null)
              .order('id', { ascending: true })
              .range(from, to),
          1000,
          { stopOnShortPage: true },
        );
        for (const sr of rows) {
          if (sr.seeding_group) seeds.set(`${sr.draw_id}:${sr.player_id}`, sr.seeding_group);
        }
      } catch (error) {
        console.error('Error loading draw seeding for public schedule (non-fatal):', error);
      }
      return seeds;
    };

    // One wave: the day's rows and everything that decorates them, plus (without a context) the
    // event's name order, venues and alias rule. The alias rule is fetched eagerly here rather than
    // only when a row carries an alias, so it never costs a round trip of its own.
    const [
      aimed,
      dayIndex,
      teamEntries,
      aimedBlobs,
      seedByDrawPlayer,
      isJapanese,
      venueCtx,
      aliasAllowed,
    ] = await Promise.all([
      readDayRows(aimedDay),
      readDayIndex(),
      readTeamEntries(),
      readLiveBlobs(aimedDay),
      readSeeding(),
      ctx ? ctx.isJapanese : readIsJapanese(),
      // Venues come from `loadVenueContextForEvent`, never an embed in the event select: it
      // tolerates a missing `event_venue` table (deploy before migration).
      ctx
        ? ctx.venueCtx
        : loadVenueContextForEvent(client, {
            id: ev.id,
            location_id: ev.location_id,
            settings: ev.settings,
          }),
      ctx ? ctx.aliasAllowed : isEventAliasAllowed(eventId, client),
    ]);

    const daySet = new Set<string>();
    for (const r of dropHiddenDrawRows(dayIndex)) {
      if (r.scheduled_datetime) daySet.add(dayFromIso(r.scheduled_datetime));
    }
    const days = [...daySet].sort();
    const selectedDay =
      (askedDay && days.includes(askedDay) ? askedDay : null) ??
      (days.includes(today) ? today : (days[0] ?? today));

    // The asked day has no rows, so the selected day fell back (today, else the first day): read
    // it, unless the caller wants exactly the day it asked for (Watch filters to its own day).
    const fellBack = selectedDay !== aimedDay && !exactDay;
    const [{ data: scheduleRows, error: schedErr }, liveBlobs] = fellBack
      ? await Promise.all([readDayRows(selectedDay), readLiveBlobs(selectedDay)])
      : [aimed, aimedBlobs];
    // Under `exactDay` with no rows on the asked day, `matches` belong to that day (none) while
    // `selectedDay` still names the fallback day; Watch keeps only rows for its own day, so this is
    // what it already did.
    const rowsDay = exactDay && selectedDay !== aimedDay ? aimedDay : selectedDay;

    if (schedErr) throw new Error(`Failed to load schedule: ${schedErr.message}`);
    // A hidden draw takes its matches off the public schedule (rows with no draw stay).
    const visibleRows = dropHiddenDrawRows(scheduleRows || []);
    type Row = NonNullable<typeof scheduleRows>[number];
    const rows: Row[] = await gateRowAliases(visibleRows, ['player1', 'player2'], () =>
      Promise.resolve(aliasAllowed),
    );
    const dayRows = rows.filter(
      (r) => !!r.scheduled_datetime && dayFromIso(r.scheduled_datetime as string) === rowsDay,
    );

    // Project matches: no ratings; stable event identities and safe display names, labelled
    // team → alias → script-aware personal name (`projectScheduleRows`).
    const matches = projectScheduleRows(dayRows as unknown as ScheduleSourceRow[], {
      liveBlobs,
      seedByDrawPlayer,
      teamEntries,
      isJapanese,
    });

    const courts = projectScheduleCourts(courtDefs, matches, venueCtx, {
      viewingToday: selectedDay === today,
      nowMs: Date.now(),
    });

    return {
      asOf,
      event: {
        id: ev.id,
        name: ev.name,
        start_date: ev.start_date,
        end_date: ev.end_date,
      },
      timezone,
      timezonePinned: pinnedZone !== null,
      days,
      selectedDay,
      courts,
      venues: venueCtx.venues.map((v) => ({
        id: v.id,
        name: v.name,
        address: v.address,
        isPrimary: v.isPrimary,
      })),
      matches,
    };
  },
};

export default eventModelV2;
